'use client';

import { useAuth, useCurrentSubscriptions } from '@/hooks/useAuth';
import { useImageUrl } from '@/hooks/useImageUrl';
import { Link } from '@tanstack/react-router';
import { Avatar, AvatarFallback, AvatarImage } from '@ui/components/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@ui/components/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@ui/components/sidebar';
import {
  BadgeCheck,
  BadgeQuestionMark,
  Bell,
  BookOpenCheck,
  BookText,
  ChevronsUpDown,
  CreditCard,
  GraduationCap,
  Home,
  LifeBuoy,
  LogInIcon,
  LogOut,
  Plus,
  Send,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';

const data = {
  navSecondary: [
    {
      title: 'Support',
      url: '#support',
      icon: LifeBuoy,
    },
    {
      title: 'Feedback',
      url: '#feedback',
      icon: Send,
    },
  ],
  projects: [
    {
      name: 'Teams',
      url: '/teams',
      icon: Users,
    },
    {
      name: 'Courses',
      url: '/courses',
      icon: Users,
      items: [
        {
          name: 'Exams',
          url: '/exams',
          icon: BookText,
          isActive: false,
        },
        {
          name: 'Grades',
          url: '#grades',
          icon: BookOpenCheck,
          isActive: false,
        },
      ],
    },
    {
      name: 'Students',
      url: '/students',
      icon: GraduationCap,
    },
    {
      name: 'Questions',
      url: '/questions',
      icon: BadgeQuestionMark,
    },
  ],
};

const dataUnProtected = {
  primary: [
    {
      name: 'Home',
      url: '/',
      icon: Home,
    },
    {
      name: 'AI Grading',
      url: '#',
      icon: BookOpenCheck,
    },
    {
      name: 'Sign In',
      url: '/sign-in',
      icon: LogInIcon,
    },
    {
      name: 'Register',
      url: '/register',
      icon: UserPlus,
    },
  ],
};

export default function Page() {
  const { isMobile } = useSidebar();
  const {
    signOut,
    useSession,
    useActiveOrganization,
    useListOrganizations,
    setActive,
    checkout,
    portal,
  } = useAuth();
  const { data: user } = useSession();
  const { data: activeOrganization } = useActiveOrganization();
  const { data: organizations } = useListOrganizations();
  const { data: customerData } = useCurrentSubscriptions();

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground border-dashed border"
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage alt={activeOrganization?.name ?? ''} />
                      <AvatarFallback className="rounded-lg">
                        {activeOrganization?.name?.substring(0, 2) ?? '??'}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{activeOrganization?.name ?? ''}</span>
                    <span className="truncate text-xs">{activeOrganization?.slug ?? ''}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-72 rounded-lg"
                align="start"
                side="right"
                sideOffset={4}
              >
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Teams
                </DropdownMenuLabel>
                {organizations &&
                  organizations.map((team, index) => (
                    <DropdownMenuItem
                      key={team.name}
                      onClick={() =>
                        setActive({
                          organizationId: team.id,
                          organizationSlug: team.slug,
                        })
                      }
                      className="gap-2 p-2"
                    >
                      <div className="flex size-6 items-center justify-center rounded-sm border gap-2">
                        <Avatar className="h-8 w-8 rounded-lg">
                          <AvatarImage alt={team?.name ?? ''} />
                          <AvatarFallback className="rounded-lg">
                            {team?.name?.substring(0, 2) ?? '??'}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      {team.name}
                      <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                    </DropdownMenuItem>
                  ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 p-2">
                  <Link to="/teams" className="flex flex-1 items-center gap-2">
                    <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                      <Plus className="size-4" />
                    </div>
                    <div className="font-medium text-muted-foreground">Add team</div>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {!user && (
          <SidebarGroup className="group-data-[collapsible=icon]:hidden">
            <SidebarGroupLabel>Welcome</SidebarGroupLabel>
            <SidebarMenu>
              {dataUnProtected.primary.map((item) => (
                <SidebarMenuItem key={item.name}>
                  <SidebarMenuButton asChild>
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.name}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}

        {user && (
          <SidebarGroup>
            <SidebarGroupLabel>Projects</SidebarGroupLabel>
            <SidebarMenu>
              {data.projects.map((project) => (
                <SidebarMenuItem key={project.name}>
                  <SidebarMenuButton asChild tooltip={project.name}>
                    <Link to={project.url}>
                      <project.icon />
                      <span>{project.name}</span>
                    </Link>
                  </SidebarMenuButton>
                  {project.items?.length ? (
                    <SidebarMenuSub>
                      {project.items.map((item) => (
                        <SidebarMenuSubItem key={item.name}>
                          <SidebarMenuSubButton asChild isActive={item.isActive}>
                            <Link to={item.url}>
                              <item.icon />
                              {item.name}
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  ) : null}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}

        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              {data.navSecondary.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild size="sm">
                    <a href={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage
                      src={user?.user.image ? useImageUrl(user?.user.image) : ''}
                      alt={user?.user.name ?? ''}
                    />
                    <AvatarFallback className="rounded-lg">
                      {user?.user.name.substring(0, 2) ?? '??'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user?.user.name ?? 'Guest'}</span>
                    <span className="truncate text-xs">{user?.user.email ?? 'No email'}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side={isMobile ? 'bottom' : 'right'}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src={user?.user.image ?? ''} alt={user?.user.name ?? ''} />
                      <AvatarFallback className="rounded-lg">
                        {user?.user.name?.substring(0, 2) ?? '??'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user?.user.name ?? 'Guest'}</span>
                      <span className="truncate text-xs">{user?.user.email ?? 'No email'}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {customerData && customerData.data?.result.items[0]?.product.name === 'Pro' ? (
                  <DropdownMenuGroup>
                    <DropdownMenuItem disabled className="cursor-not-allowed opacity-50">
                      <Sparkles className="text-muted-foreground" />
                      <span>Pro Plan</span>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                ) : (
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() =>
                        checkout({
                          products: [
                            'ca8525b3-a66b-444a-98ab-cd59566ef2d4',
                            'd5a3c05c-c17a-4cde-b69b-86577aab579c',
                          ],
                        })
                      }
                    >
                      <Sparkles />
                      Upgrade to Pro
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem>
                    <BadgeCheck />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      portal({
                        query: { returnURL: window.location.origin },
                      })
                    }
                  >
                    <CreditCard />
                    Billing
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Bell />
                    Notifications
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
